import React, { cloneElement, isValidElement, useCallback } from 'react';
import { withTooltip } from '../../hoc';
import { Button as ChakraUiButton, Span } from '@chakra-ui/react';
import { ButtonProps } from './types';

// Chakra's Button has no leftIcon/rightIcon props, so this wraps each icon in a `chakra-button__icon` span
// (styled by the Button recipe and SCSS) and clones `aria-hidden` onto it to keep it out of the a11y tree.
const ButtonIcon: React.FC<{ children: React.ReactNode; placement: 'start' | 'end' }> = ({ children, placement }) => (
  <Span
    className="chakra-button__icon"
    display="inline-flex"
    alignSelf="center"
    flexShrink={0}
    data-placement={placement}
  >
    {isValidElement<React.AriaAttributes & { focusable?: boolean }>(children)
      ? cloneElement(children, { 'aria-hidden': true, focusable: false })
      : children}
  </Span>
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    data = {},
    href,
    targetBlank,
    onClick,
    isFullWidth,
    size,
    variant,
    isDisabled,
    disabled,
    isActive,
    active,
    isInvert,
    colorScheme,
    colorPalette,
    tooltip,
    leftIcon,
    rightIcon,
    color,
    ...rest
  } = props;

  const clickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (href) {
        if (targetBlank) {
          window.open(href, '_blank', 'noopener,noreferrer');
        } else {
          window.location.assign(href);
        }
      }
      onClick?.(e);
    },
    [href, onClick]
  );

  // Chakra's `_active` matches `[data-active]` by presence — false must omit the attribute, not set "false".
  const activeAttr = (isActive ?? active) ? { 'data-active': '' } : undefined;
  // An unlayered `color` prop outranks the recipe's layered `_disabled` ink, so a disabled button drops it.
  const isButtonDisabled = isDisabled ?? disabled;

  /**
   * DOM
   */
  return (
    <ChakraUiButton
      data-object-type="button"
      ref={ref}
      onClick={clickHandler}
      size={size as never}
      variant={variant as never}
      disabled={isButtonDisabled}
      color={isButtonDisabled ? undefined : color}
      invert={isInvert as never}
      colorPalette={colorScheme ?? colorPalette}
      {...activeAttr}
      {...(href && { as: 'a' })}
      {...(isFullWidth && { width: 'full', flexGrow: '1' })}
      {...rest}
    >
      {leftIcon && <ButtonIcon placement="start">{leftIcon}</ButtonIcon>}
      {props.children}
      {rightIcon && <ButtonIcon placement="end">{rightIcon}</ButtonIcon>}
    </ChakraUiButton>
  );
});

Button.displayName = 'Button';

export default withTooltip<HTMLButtonElement>('CoreButton')(Button);
